import axios, { AxiosResponse } from 'axios';
import { LoginRequest, LoginResponse, SelectEmpresaSoporteRequest, SelectSucursalRequest } from '../models/auth';
import { TokenService } from './tokenService';

const authApi = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8080'),
  timeout: 10000,
  // La cookie HttpOnly del refresh token viaja (y se recibe) solo con credenciales
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

export class AuthService {

  /** Guarda token y usuario cuando la respuesta trae sesión; los pasos intermedios se devuelven tal cual. */
  private static procesarLogin(data: LoginResponse): LoginResponse {
    if (data.requiresTotp || data.requiresSucursalSelection || data.requiresEmpresaSoporteSelection) {
      // Falta el código 2FA, la sucursal o la empresa de soporte: no guardamos token aún
      return data;
    }

    if (data?.token) {
      TokenService.setToken(data.token);
      TokenService.setUser({
        username: data.username,
        empresaId: data.empresaId,
        sucursalId: data.sucursalId,
        sucursalNombre: data.sucursalNombre,
        empresaNombre: data.empresaNombre,
        isAuthenticated: true,
        esSoporte: data.esSoporte ?? false,
        soporteEscritura: data.soporteEscritura ?? false,
        requiereCambioPassword: data.requiereCambioPassword ?? false,
      });
    }

    return data;
  }

  private static errorDeLogin(error: any, porDefecto: string): Error {
    if (error.response) {
      return new Error(error.response.data?.message || porDefecto);
    } else if (error.request) {
      return new Error('Error de conexión. Verifique su conexión a internet.');
    }
    return new Error('Error inesperado durante el login');
  }

  static async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response: AxiosResponse<LoginResponse> = await authApi.post('/api/auth/login', credentials);
      return AuthService.procesarLogin(response.data);
    } catch (error: any) {
      throw AuthService.errorDeLogin(error, 'Credenciales inválidas');
    }
  }

  /** Segundo paso del login con 2FA: código de la app (o uno de respaldo). */
  static async verifyTotp(totpToken: string, codigo: string, recordarDispositivo = false): Promise<LoginResponse> {
    try {
      const response: AxiosResponse<LoginResponse> = await authApi.post('/api/auth/verify-totp', { totpToken, codigo, recordarDispositivo });
      return AuthService.procesarLogin(response.data);
    } catch (error: any) {
      throw AuthService.errorDeLogin(error, 'Código incorrecto o sesión expirada. Inicie sesión de nuevo.');
    }
  }

  // ── 2FA del propio usuario (requiere sesión) ──────────────────────────────
  private static async conSesion<T>(method: 'get' | 'post', url: string, body?: any): Promise<T> {
    try {
      const headers = { Authorization: `Bearer ${TokenService.getToken()}` };
      const response = method === 'get' ? await authApi.get(url, { headers }) : await authApi.post(url, body, { headers });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'No se pudo completar la operación');
    }
  }

  static totpEstado(): Promise<{ activo: boolean; respaldoRestantes: number; dispositivosConfiables: number }> {
    return AuthService.conSesion('get', '/api/auth/2fa/estado');
  }

  static async totpOlvidarDispositivos(): Promise<void> {
    await AuthService.conSesion('post', '/api/auth/2fa/olvidar-dispositivos');
  }

  static totpIniciar(): Promise<{ secreto: string; qrBase64: string }> {
    return AuthService.conSesion('post', '/api/auth/2fa/iniciar');
  }

  static totpActivar(codigo: string): Promise<{ codigosRespaldo: string[] }> {
    return AuthService.conSesion('post', '/api/auth/2fa/activar', { codigo });
  }

  static async totpDesactivar(password: string, codigo: string): Promise<void> {
    await AuthService.conSesion('post', '/api/auth/2fa/desactivar', { password, codigo });
  }

  static async selectSucursal(preAuthToken: string, sucursalId: number): Promise<LoginResponse> {
    try {
      const body: SelectSucursalRequest = { preAuthToken, sucursalId };
      const response: AxiosResponse<LoginResponse> = await authApi.post('/api/auth/select-sucursal', body);

      if (response.data?.token) {
        TokenService.setToken(response.data.token);
        TokenService.setUser({
          username: response.data.username,
          empresaId: response.data.empresaId!,
          sucursalId: response.data.sucursalId,
          sucursalNombre: response.data.sucursalNombre,
          empresaNombre: response.data.empresaNombre,
          isAuthenticated: true,
          esSoporte: response.data.esSoporte ?? false,
          soporteEscritura: response.data.soporteEscritura ?? false,
          requiereCambioPassword: response.data.requiereCambioPassword ?? false,
        });
      }

      return response.data;
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || 'Error al seleccionar sucursal');
      }
      throw new Error('Error de conexión.');
    }
  }

  static async selectEmpresaSoporte(preAuthToken: string, empresaIdDestino: number): Promise<LoginResponse> {
    try {
      const body: SelectEmpresaSoporteRequest = { preAuthToken, empresaIdDestino };
      const response: AxiosResponse<LoginResponse> = await authApi.post('/api/auth/select-empresa-soporte', body);

      if (response.data?.token) {
        TokenService.setToken(response.data.token);
        TokenService.setUser({
          username: response.data.username,
          empresaId: response.data.empresaId!,
          sucursalId: response.data.sucursalId,
          sucursalNombre: response.data.sucursalNombre,
          empresaNombre: response.data.empresaNombre,
          isAuthenticated: true,
          esSoporte: true,
          soporteEscritura: response.data.soporteEscritura ?? false,
        });
      }

      return response.data;
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || 'Error al seleccionar empresa de soporte');
      }
      throw new Error('Error de conexión.');
    }
  }

  static async solicitarRecuperacion(email: string): Promise<void> {
    await authApi.post('/api/auth/recuperar-password/solicitar', { email });
  }

  static async verificarRecuperacion(email: string, codigo: string, passwordNueva: string): Promise<void> {
    await authApi.post('/api/auth/recuperar-password/verificar', { email, codigo, passwordNueva });
  }

  static async cambiarPassword(passwordActual: string, passwordNueva: string): Promise<void> {
    const token = TokenService.getToken();
    await authApi.post(
      '/api/auth/cambiar-password',
      { passwordActual, passwordNueva },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  }

  static logout(): void {
    // Revoca el refresh token y borra la cookie; si falla igual se cierra la sesión local
    authApi.post('/api/auth/logout').catch(() => undefined);
    TokenService.removeToken();
  }

  static getCurrentUser(): any | null {
    return TokenService.getUser();
  }

  static isAuthenticated(): boolean {
    return TokenService.isTokenValid();
  }

  static async refreshToken(): Promise<string | null> {
    try {
      // La credencial es la cookie HttpOnly del refresh token; el JS no la ve ni la envía a mano
      const response: AxiosResponse<{ token: string }> = await authApi.post('/api/auth/refresh');

      if (response.data?.token) {
        TokenService.setToken(response.data.token);
        return response.data.token;
      }

      return null;
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.logout();
      return null;
    }
  }

  static getAuthHeader(): { Authorization: string } | {} {
    const token = TokenService.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  static async validateSession(): Promise<boolean> {
    try {
      const token = TokenService.getToken();
      if (!token) return false;

      const response: AxiosResponse<LoginResponse> = await authApi.get('/api/auth/validate', {
        headers: this.getAuthHeader(),
      });

      // Actualizar datos de nombre en el user almacenado si el token los trae
      if (response.data) {
        const stored = TokenService.getUser();
        if (stored) {
          TokenService.setUser({
            ...stored,
            sucursalNombre: response.data.sucursalNombre,
            empresaNombre: response.data.empresaNombre,
            requiereCambioPassword: response.data.requiereCambioPassword ?? false,
          });
        }
      }

      return true;
    } catch (error) {
      console.error('Session validation failed:', error);
      this.logout();
      return false;
    }
  }
}
